<template>
  <v-dialog v-model="isOpen" persistent max-width="600">
    <v-card class="align-center text-center">
      <v-card-title class="headline justify-center"> {{ bodyText }}</v-card-title>
      <div v-if="!isBoarding">
        <v-card-text class="align-center text-left">
          <v-card-subtitle class="w-100 text-subtitle-1 font-weight-regular pt-4 pl-0">
            Tipo da carga:
          </v-card-subtitle>
          <v-radio-group v-model="selectedType" row mandatory>
            <v-radio label="Entrega direta" :value="true"></v-radio>
            <v-radio label="Transferência" :value="false"></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-text v-if="selectedType === false" class="d-flex align-center text-left px-2 pb-0">
          <v-col cols="12" md="12" class="pb-0">
            <v-autocomplete
              v-model="cityId"
              ref="selectCity"
              label="Cidade do Transbordo (Obrigatório)"
              :items="formattedTranshipmentCities"
              item-text="displayText"
              item-value="cityId"
              :rules="[rules.required]"
              :loading="loadingCity"
              clearable
            >
            </v-autocomplete>
          </v-col>
        </v-card-text>
        <v-card-text v-if="selectedType === false" class="w-100 text-end px-4 py-0 pb-1">
          <a href="#/transhipments/new" target="_blank"> Clique aqui adicionar um novo transbordo </a>
        </v-card-text>
      </div>
      <div v-else>
        <v-card-text class="align-center text-left">
          <v-card-subtitle class="w-100 text-subtitle-1 font-weight-regular pt-4 pl-0"> Dados Gerais: </v-card-subtitle>
          <v-card-text v-if="isLoading" class="py-12 d-flex align-center justify-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </v-card-text>
          <v-row v-else>
            <v-col cols="12">
              <v-autocomplete
                ref="directDeliverer"
                v-model="directDelivererId"
                :items="getSummaryData"
                label="Motorista Direto"
                item-text="name"
                item-value="id"
                :rules="[rules.required]"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                ref="freightPrice"
                v-model="freightPrice"
                label="Preço do frete embarcador"
                v-money="money"
                :rules="hasInteractedWithFreight ? [rules.hasPrice] : []"
                @blur="hasInteractedWithFreight = true"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                ref="totalMileage"
                v-model="totalMileage"
                v-money="totalMileageMask"
                label="Quilometragem total"
                :rules="hasInteractedWithMileage ? [rules.hasMileage] : []"
                @blur="hasInteractedWithMileage = true"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </div>
      <v-card-actions class="d-flex justify-space-between pb-5">
        <v-btn @click="$emit('update:isOpen'), cancel()" color="error" class="flex-grow-1">Cancelar</v-btn>
        <v-tooltip bottom :disabled="!checkForm().disabled">
          <template #activator="{ on, attrs }">
            <div class="flex-grow-1 ml-2" v-bind="attrs" v-on="on">
              <v-btn
                :loading="isConfirmBtnLoading"
                :disabled="checkForm().disabled"
                @click="confirmSelection"
                class="w-100"
                color="success"
              >
                {{ confirmButtonText }}
              </v-btn>
            </div>
          </template>
          <span>{{ checkForm().text }}</span>
        </v-tooltip>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { VMoney } from 'v-money';
import { mapActions, mapGetters } from 'vuex';
import { stringToMoneyMask } from '@/handlers/MoneyHandler.js';
import removePriceMask from '@/utils/removePriceMask';

export default {
  props: {
    isOpen: {
      default: true,
      type: Boolean
    },
    isBoarding: {
      default: false,
      type: Boolean
    },
    boardingData: {
      default: null,
      type: Object
    },
    isGrouping: {
      default: false,
      type: Boolean
    },
    confirmButtonText: {
      default: 'Confirmar',
      type: String
    },
    isConfirmBtnLoading: {
      default: false,
      type: Boolean
    }
  },
  directives: { money: VMoney },
  data() {
    return {
      directDelivererId: null,
      freightPrice: null,
      totalMileage: null,
      transhipmentCities: [],
      selectedType: null,
      cityId: null,
      isLoading: false,
      loadingCity: false,
      hasInteractedWithFreight: false,
      hasInteractedWithMileage: false,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: '',
        precision: 2,
        masked: false
      },
      totalMileageMask: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 0,
        masked: false
      },
      rules: {
        required: (value) => !!value || 'Campo obrigatório!',
        hasMileage: (value) => {
          return value >= 0 || 'Quilometragem deve ser maior ou igual a 0!';
        },
        hasPrice: (value) => {
          return value !== 'R$ 0,00' || 'Campo obrigatório!';
        }
      }
    };
  },
  watch: {
    async isOpen(val) {
      if (val) {
        this.reset();
        if (this.isBoarding) {
          window.removeEventListener('focus', this.handleWindowFocus);
          if (this.boardingData != null) {
            this.isLoading = true;
            await this.fetchSummaryData();
            this.freightPrice = stringToMoneyMask(this.boardingData.freightPrice);
            this.totalMileage = this.boardingData.totalMileage;
            const matchingDriver = this.getSummaryData.find(
              (driver) => driver.name === this.boardingData.directDelivererName
            );
            if (matchingDriver) {
              this.directDelivererId = matchingDriver.id;
            }
            this.isLoading = false;
          } else {
            await this.fetchSummaryData();
          }
        } else {
          window.addEventListener('focus', this.handleWindowFocus);
          await this.fetchTranshipment();
        }
      }
    }
  },
  mounted() {
    if (this.isOpen && !this.isBoarding) {
      window.addEventListener('focus', this.handleWindowFocus);
    }
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.handleWindowFocus);
  },
  computed: {
    ...mapGetters('CouriersStore', ['getSummaryData']),
    bodyText() {
      if (this.isBoarding) {
        return this.isGrouping
          ? 'Agrupar Embarques'
          : this.boardingData != null
          ? 'Editar Embarque'
          : 'Adicionar Embarque';
      } else {
        return this.isGrouping ? 'Agrupar Cargas' : 'Adicionar Carga';
      }
    },
    formattedTranshipmentCities() {
      return this.transhipmentCities.map((city) => {
        return {
          cityId: city.cityId,
          displayText: `${city.cityName} - ${city.ufName} [${city.transhipmentName}]`
        };
      });
    }
  },
  methods: {
    ...mapActions('TranshipmentStore', ['fetchTranshipmentCities']),
    ...mapActions('CouriersStore', ['fetchSummaryData']),
    handleWindowFocus() {
      if (this.isOpen && !this.isBoarding) {
        this.fetchTranshipment();
      }
    },
    async fetchTranshipment() {
      this.loadingCity = true;
      try {
        if (!this.isBoarding) this.transhipmentCities = await this.fetchTranshipmentCities();
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingCity = false;
      }
    },
    reset() {
      this.selectedType = null;
      this.cityId = null;
      this.directDelivererId = null;
      this.freightPrice = null;
      this.totalMileage = null;
      if (this.$refs.freightPrice) {
        this.$refs.freightPrice.$el.getElementsByTagName('input')[0].value = 0;
        this.$refs.totalMileage.$el.getElementsByTagName('input')[0].value = 0;
      }
      this.$refs.directDeliverer && this.$refs.directDeliverer.resetValidation();
      this.hasInteractedWithFreight = false;
      this.hasInteractedWithMileage = false;
      this.loadingCity = false;
    },
    async cancel() {
      await this.$emit('cancelClick');
      this.reset();
    },
    checkInvalid(campo) {
      if (campo == null) {
        return true;
      } else return false;
    },
    transformPriceToSend(element) {
      if (!element) return null;
      var money = element.toString().trim();
      money = money.replace('R$ ', '').replaceAll('.', '').replace(',', '.');
      return Number(money);
    },
    checkForm() {
      if (this.isBoarding) {
        if (
          this.totalMileage == null ||
          this.directDelivererId == null ||
          this.freightPrice == 0 ||
          this.freightPrice == 'R$ 0,00'
        ) {
          return { disabled: true, text: 'Prencha os campos obrigatórios' };
        }
      } else {
        if (this.selectedType === null) {
          return { disabled: true, text: 'Selecione o tipo de carga' };
        } else if (this.selectedType === true) {
          return { disabled: false, text: '' };
        } else if (this.checkInvalid(this.cityId)) {
          return { disabled: true, text: 'Selecionada o Transbordo' };
        }
      }

      return { disabled: false, text: '' };
    },
    confirmSelection() {
      const parsePrice = (value) => Number(removePriceMask(value));
      if (this.isBoarding) {
        const response = {
          directDelivererId: this.directDelivererId,
          freightPrice: parsePrice(this.freightPrice),
          totalMileage: Number(this.totalMileage)
        };
        if (this.boardingData != null) {
          this.$emit('confirmEditBoardingClick', response);
        } else {
          this.$emit('confirmBoardingClick', response);
        }
      } else {
        this.$emit('confirmCargoClick', false, {
          isDirectDelivery: this.selectedType,
          cityId: this.cityId
        });
      }
      this.cancel();
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-input__slot {
  margin: 0;
  padding: 0;

  &::after,
  &::before {
    display: none !important;
  }
}
::v-deep.v-input {
  .v-text-field {
    &__details {
      padding-top: 4px;
      display: inline-block;
    }
  }
}

::v-deep .v-input__append-inner.v-input__append-inner .v-input__icon--clear {
  padding-top: 17px;
}
</style>
